package com.upcominds.expedite.data.challenges

import com.upcominds.expedite.data.NetworkClient
import com.upcominds.expedite.data.challenges.remote.FinishQuizRequest
import com.upcominds.expedite.data.challenges.remote.StartQuizResponse
import com.upcominds.expedite.data.challenges.remote.SubmitQuizAnswerRequest
import com.upcominds.expedite.data.challenges.remote.SubmitQuizAnswerResponse
import com.upcominds.expedite.data.documents.PickedDocument
import com.upcominds.expedite.model.Challenge
import com.upcominds.expedite.model.ChallengeProgress
import com.upcominds.expedite.model.Quiz
import com.upcominds.expedite.model.QuizAttemptResult
import com.upcominds.expedite.dto.QuestionnaireAttemptDTO
import com.upcominds.expedite.dto.SavingsBillUploadDTO
import com.upcominds.expedite.dto.SavingsBillUploadResultDTO
import com.upcominds.expedite.dto.SavingsChallengeProgressDTO
import com.upcominds.expedite.dto.SocialChallengeSubmissionDTO
import com.upcominds.expedite.dto.SocialChallengeVerifyResultDTO
import com.upcominds.expedite.dto.SocialPostVerifyRequest
import com.upcominds.expedite.dto.SubmitQuestionnaireAnswerRequest
import com.upcominds.expedite.dto.PuzzleAttemptDTO
import com.upcominds.expedite.dto.PuzzleResultDTO
import com.upcominds.expedite.dto.CommunityCompetitionDTO
import com.upcominds.expedite.dto.CommunityCompetitionLeaderboardDTO
import com.upcominds.expedite.dto.CompetitionRepresentationDTO
import com.upcominds.expedite.dto.CompetitionRepresentationSelectionRequest
import com.upcominds.expedite.dto.SubmitPuzzleAnswerRequest
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.request.delete
import io.ktor.client.request.forms.MultiPartFormDataContent
import io.ktor.client.request.forms.formData
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType

class ChallengesRepositoryImpl : ChallengesRepository {

    private val httpClient: HttpClient = NetworkClient.httpClient

    override suspend fun getChallenges(): List<Challenge> {
        return httpClient.get(NetworkClient.endpoint("api/challenges/me/community-challenges")).body<List<Challenge>>()
    }

    override suspend fun getChallenge(challengeId: String): Challenge {
        return httpClient.get(NetworkClient.endpoint("api/challenges/$challengeId")).body<Challenge>()
    }

    override suspend fun getChallengeParticipated(): List<Challenge> {
        return httpClient.get(NetworkClient.endpoint("api/challenges/me/participated")).body<List<Challenge>>()
    }

    override suspend fun getCommunityCompetitions(): List<CommunityCompetitionDTO> {
        return httpClient.get(NetworkClient.endpoint("api/competitions")).body<List<CommunityCompetitionDTO>>()
    }

    override suspend fun getCommunityCompetition(id: String): CommunityCompetitionDTO {
        return httpClient.get(NetworkClient.endpoint("api/competitions/$id")).body<CommunityCompetitionDTO>()
    }

    override suspend fun getCommunityCompetitionLeaderboard(id: String): CommunityCompetitionLeaderboardDTO {
        return httpClient.get(NetworkClient.endpoint("api/competitions/$id/leaderboard"))
            .body<CommunityCompetitionLeaderboardDTO>()
    }

    override suspend fun getCompetitionRepresentation(id: String): CompetitionRepresentationDTO {
        return httpClient.get(NetworkClient.endpoint("api/competitions/$id/representation"))
            .body<CompetitionRepresentationDTO>()
    }

    override suspend fun selectCompetitionRepresentation(
        id: String,
        communityId: String
    ): CompetitionRepresentationDTO {
        return httpClient.put(NetworkClient.endpoint("api/competitions/$id/representation")) {
            contentType(ContentType.Application.Json)
            setBody(CompetitionRepresentationSelectionRequest(communityId))
        }.body<CompetitionRepresentationDTO>()
    }

    override suspend fun getSavingsProgress(challengeId: String): SavingsChallengeProgressDTO {
        return httpClient.get(NetworkClient.endpoint("api/challenges/${challengeId}/savings/progress"))
            .body<SavingsChallengeProgressDTO>()
    }

    override suspend fun getSavingsBills(challengeId: String): List<SavingsBillUploadDTO> {
        return httpClient.get(NetworkClient.endpoint("api/challenges/${challengeId}/savings/bills"))
            .body<List<SavingsBillUploadDTO>>()
    }

    override suspend fun uploadSavingsBill(challengeId: String, file: PickedDocument): SavingsBillUploadResultDTO {
        return httpClient.post(NetworkClient.endpoint("api/challenges/${challengeId}/savings/bills")) {
            setBody(
                MultiPartFormDataContent(
                    formData {
                        append(
                            key = "file",
                            value = file.bytes,
                            headers = Headers.build {
                                append(HttpHeaders.ContentType, file.mimeType ?: "application/pdf")
                                append(HttpHeaders.ContentDisposition, "filename=\"${file.fileName}\"")
                            }
                        )
                    }
                )
            )
        }.body<SavingsBillUploadResultDTO>()
    }

    override suspend fun deleteSavingsBill(challengeId: String, billId: String): SavingsChallengeProgressDTO {
        return httpClient.delete(NetworkClient.endpoint("api/challenges/${challengeId}/savings/bills/${billId}"))
            .body<SavingsChallengeProgressDTO>()
    }

    override suspend fun verifySocialPost(challengeId: String, postUrl: String): SocialChallengeVerifyResultDTO {
        return httpClient.post(NetworkClient.endpoint("api/challenges/${challengeId}/social/verify")) {
            contentType(ContentType.Application.Json)
            setBody(SocialPostVerifyRequest(postUrl = postUrl))
        }.body<SocialChallengeVerifyResultDTO>()
    }

    override suspend fun getSocialSubmissions(challengeId: String): List<SocialChallengeSubmissionDTO> {
        return httpClient.get(NetworkClient.endpoint("api/challenges/${challengeId}/social/submissions"))
            .body<List<SocialChallengeSubmissionDTO>>()
    }

    override suspend fun start(challengeId: String): ChallengeProgress {
        return httpClient.post(NetworkClient.endpoint("api/challenges/${challengeId}/start")) {
            contentType(ContentType.Application.Json)
        }.body<ChallengeProgress>()
    }

    override suspend fun complete(challengeId: String): ChallengeProgress {
        return httpClient.post(NetworkClient.endpoint("api/challenges/${challengeId}/complete")) {
            contentType(ContentType.Application.Json)
        }.body<ChallengeProgress>()
    }

    override suspend fun getChallengesByCommunity(communityId: String): List<Challenge> {
        return httpClient.get(NetworkClient.endpoint("api/communities/${communityId}/challenges")).body<List<Challenge>>()
    }

    override suspend fun getQuizzesByChallenge(challengeId: String): List<Quiz> {
        return httpClient.get(NetworkClient.endpoint("api/challenges/${challengeId}/quizzes")).body<List<Quiz>>()
    }

    // Questionnaire
    override suspend fun questionnaireStart(challengeId: String): QuestionnaireAttemptDTO {
        return httpClient.post(NetworkClient.endpoint("api/questionnaires/challenge/${challengeId}/start")) {
            contentType(ContentType.Application.Json)
        }.body<QuestionnaireAttemptDTO>()
    }

    override suspend fun questionnaireReview(challengeId: String): QuestionnaireAttemptDTO {
        return httpClient.get(NetworkClient.endpoint("api/questionnaires/challenge/${challengeId}/review"))
            .body<QuestionnaireAttemptDTO>()
    }

    override suspend fun questionnaireSubmitAnswer(attemptId: String, questionId: String, optionId: String) {
        httpClient.post(NetworkClient.endpoint("api/questionnaires/attempt/${attemptId}/submit")) {
            contentType(ContentType.Application.Json)
            setBody(SubmitQuestionnaireAnswerRequest(questionId = questionId, optionId = optionId))
        }
    }

    override suspend fun questionnaireComplete(attemptId: String) {
        httpClient.post(NetworkClient.endpoint("api/questionnaires/attempt/${attemptId}/complete")) {
            contentType(ContentType.Application.Json)
        }
    }

    // Puzzle
    override suspend fun puzzleStart(challengeId: String): PuzzleAttemptDTO {
        return httpClient.post(NetworkClient.endpoint("api/puzzles/challenge/${challengeId}/start")) {
            contentType(ContentType.Application.Json)
        }.body<PuzzleAttemptDTO>()
    }

    override suspend fun puzzleSubmitAnswer(attemptId: String, questionId: String, optionId: String) {
        httpClient.post(NetworkClient.endpoint("api/puzzles/attempt/${attemptId}/submit")) {
            contentType(ContentType.Application.Json)
            setBody(SubmitPuzzleAnswerRequest(questionId = questionId, optionId = optionId))
        }
    }

    override suspend fun puzzleComplete(attemptId: String): PuzzleResultDTO {
        return httpClient.post(NetworkClient.endpoint("api/puzzles/attempt/${attemptId}/complete")) {
            contentType(ContentType.Application.Json)
        }.body<PuzzleResultDTO>()
    }

    // Quiz
    override suspend fun getQuizForChallenge(challengeId: String): Quiz {
        return httpClient.get(NetworkClient.endpoint("api/quizzes/by-challenge/${challengeId}")).body<Quiz>()
    }

    override suspend fun quizStart(quizId: String): StartQuizResponse {
        return httpClient.post(NetworkClient.endpoint("api/quizzes/${quizId}/start")) {
            contentType(ContentType.Application.Json)
        }.body<StartQuizResponse>()
    }

    override suspend fun quizSubmitAnswer(
        quizId: String,
        attemptId: String,
        questionId: String,
        optionId: String,
    ): SubmitQuizAnswerResponse {
        return httpClient.post(NetworkClient.endpoint("api/quizzes/${quizId}/answer")) {
            contentType(ContentType.Application.Json)
            setBody(SubmitQuizAnswerRequest(
                attemptId = attemptId,
                questionId = questionId,
                optionId = optionId
            ))
        }.body<SubmitQuizAnswerResponse>()
    }

    override suspend fun quizFinish(
        quizId: String,
        attemptId: String
    ): QuizAttemptResult {
        return httpClient.post(NetworkClient.endpoint("api/quizzes/${quizId}/finish")) {
            contentType(ContentType.Application.Json)
            setBody(FinishQuizRequest(
                attemptId = attemptId
            ))
        }.body<QuizAttemptResult>()
    }

    override suspend fun getQuizAttempts(quizId: String): List<QuizAttemptResult> {
        return httpClient.get(NetworkClient.endpoint("api/quizzes/${quizId}/attempts")).body<List<QuizAttemptResult>>()
    }
}
